import { Fragment, useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { api, MUHASEBE_AD, formatTarih, formatTl } from '../lib/api';
import { useAuth } from '../lib/auth';
import Ipucu from '../ui/Ipucu';
import { useDonemYillari } from '../ui/DonemSecici';

/**
 * Ciro & Bakiyeler — ay ay satış cirosu ve alış toplamı, cari borç/alacak.
 * Rakamlar Paraşüt faturalarından gelir; panel yalnız toplar.
 */
type Ay = {
  ay: string; satisAdet: number; iadeAdet: number; ciroHaricKurus: number; ciroDahilKurus: number; satisKalanKurus: number;
  alisAdet: number; alisHaricKurus: number; alisDahilKurus: number; alisKalanKurus: number;
  faturasizAdet: number; faturasizKurus: number;
  ciroBrutKurus: number; faturaIskontoKurus: number;
  numarasizAdet: number; numarasizKurus: number; ileriTarihliAdet: number; ileriTarihliKurus: number;
};
type Aylik = { aylar: Ay[]; faturasizGorunur: boolean; ilkFaturaTarihi: string | null };
type Musteri = { id: number; code: string | null; name: string; bakiyeKurus: number | null; acikFaturaKurus: number;
  acikFaturaAdet: number; enEskiVade: string | null; faturasizKurus?: number };
type Tedarikci = { id: number; name: string; bakiyeKurus: number | null; acikFaturaKurus: number; acikFaturaAdet: number };
type Bakiye = { musteriler: Musteri[]; tedarikciler: Tedarikci[]; faturasizGorunur: boolean; bakiyeTarihi: string | null;
  ozet: { alacakKurus: number; musteriAvansKurus: number; borcKurus: number; faturasizKurus: number | null } };

type CariYil = { id: number; code: string | null; name: string; type: string; sorumluId: number | null; sorumlu: string | null;
  ciroKurus: number; faturaAdet: number; kalanKurus: number; sonFatura: string | null; hedefKurus: number | null;
  gerceklesmePct: number | null; beklenenPct: number | null; acikTeklifKurus: number; acikTeklifAdet: number };
type CariYillik = { yil: number; cariler: CariYil[]; kullanicilar: { id: number; name: string }[]; yilGecenPct: number;
  ozet: { ciroKurus: number; hedefKurus: number; hedefliCiroKurus: number; hedefliAdet: number; cariAdet: number } };
type Temsilci = { id: number | null; ad: string; musteriAdet: number; ciroKurus: number; faturaAdet: number; kalanKurus: number;
  teklifAdet: number; teklifKurus: number; kazanilanAdet: number; kazanilanKurus: number; kaybedilenAdet: number; acikTeklifAdet: number;
  aktiviteAdet: number; donusumPct: number | null; musteriler: { id: number; name: string; ciroKurus: number; faturaAdet: number; kalanKurus: number }[] };
type Urun = { ad: string; birim: string | null; miktar: number; tutarKurus: number; faturaAdet: number; cariAdet: number; artKodu: string | null };

const AYLAR = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];
const ayAdi = (ay: string) => `${AYLAR[Number(ay.slice(5)) - 1]} ${ay.slice(0, 4)}`;

/** Artı bakiye: cari bize borçlu. Eksi: biz ona borçluyuz. */
function BakiyeHucre({ kurus }: { kurus: number | null }) {
  if (kurus == null) return <span className="ccd-muted">—</span>;
  if (kurus === 0) return <span className="ccd-muted">₺0</span>;
  return kurus > 0
    ? <span style={{ color: 'var(--ccd-danger)' }}>{formatTl(kurus)} <span className="ccd-meta">bize borçlu</span></span>
    : <span>{formatTl(-kurus)} <span className="ccd-meta">biz borçluyuz</span></span>;
}

export default function Finans() {
  /** Bu sayfa TANIM ve BAKİYE içindir; dönem raporları Raporlar'dadır. Eski sekme adresleri oraya yönlenir. */
  const istenen = new URLSearchParams(window.location.search).get('sekme');
  const YONLEN: Record<string, string> = { aylik: '/rapor/finans', temsilci: '/rapor/temsilciler', urun: '/rapor/urunler', stok: '/rapor/stok' };
  const [sekme, setSekme] = useState<'cari' | 'kategori' | 'musteri' | 'tedarikci'>(
    istenen === 'kategori' || istenen === 'musteri' || istenen === 'tedarikci' ? istenen : 'cari');
  type Kat = { ad: string; adet: number; haricKurus: number; kalanKurus: number; stokDisi: boolean };
  const [kat, setKat] = useState<{ satis: Kat[]; alis: Kat[]; sonTazeleme: string | null } | null>(null);
  const { can } = useAuth();
  const yazabilir = can('settings.write');
  const [yil, setYil] = useState(new Date().getFullYear());
  const donemYillari = useDonemYillari();
  const [cariYil, setCariYil] = useState<CariYillik | null>(null);
  const [temsilci, setTemsilci] = useState<Temsilci[] | null>(null);
  const [urunler, setUrunler] = useState<Urun[] | null>(null);
  const [acikTemsilci, setAcikTemsilci] = useState<number | null | undefined>(undefined);
  const [hedefDuzen, setHedefDuzen] = useState<{ id: number; deger: string } | null>(null);
  const cariYukle = (y: number) => api<CariYillik>(`/api/finans/cari-yillik?yil=${y}`).then(setCariYil).catch((e) => setHata((e as Error).message));
  useEffect(() => {
    setCariYil(null); setTemsilci(null); setUrunler(null);
    void cariYukle(yil);
    setKat(null);
    api<NonNullable<typeof kat>>(`/api/finans/kategoriler?yil=${yil}`).then(setKat).catch(() => undefined);
  }, [yil]);
  async function cariYaz(id: number, govde: Record<string, unknown>) {
    setHata(null);
    try { await api(`/api/finans/cari/${id}`, { method: 'PATCH', body: JSON.stringify('hedefTl' in govde ? { ...govde, yil } : govde) }); await cariYukle(yil);
 }
    catch (e) { setHata(e instanceof Error ? e.message : 'Kaydedilemedi'); }
  }
  const [aylik, setAylik] = useState<Aylik | null>(null);
  const [bakiye, setBakiye] = useState<Bakiye | null>(null);
  const [hata, setHata] = useState<string | null>(null);
  const [ara, setAra] = useState('');

  useEffect(() => {
    api<Aylik>('/api/finans/aylik').then(setAylik).catch((e) => setHata((e as Error).message));
    api<Bakiye>('/api/finans/bakiyeler').then(setBakiye).catch((e) => setHata((e as Error).message));
  }, []);

  const suz = <T extends { name: string }>(xs: T[]) =>
    ara.trim() ? xs.filter((x) => x.name.toLocaleLowerCase('tr').includes(ara.trim().toLocaleLowerCase('tr'))) : xs;

  if (istenen && YONLEN[istenen]) return <Navigate to={YONLEN[istenen]} replace />;

  return (
    <div className="ccd-stack ccd-fin-sayfa">
      <div>
        <div className="ccd-row" style={{ justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
          <h1 className="ccd-title">Tanımlar ve bakiyeler</h1>
        </div>
        <div className="ccd-meta">
          Cari hedefleri ve sorumluları, müşteri ve tedarikçi bakiyeleri, stok tutmayan kategoriler. Ciro, satış temsilcisi, ürün ve stok değeri raporları <Link to="/rapor/finans" className="ccd-link">Raporlar</Link>'da.
        </div>
        <details className="ccd-fin-aciklama">
          <summary>Bu rakamlar nasıl hesaplanır?</summary>
          <ul>
            <li><b>Ciro</b>, {MUHASEBE_AD} faturalarından gelir: fatura iskontosu düşüldükten sonraki KDV hariç tutardır.</li>
            <li>{MUHASEBE_AD} ekranı iskonto <i>öncesi</i> ara toplamı gösterir; aradaki fark ilgili ayın altında yazar.</li>
            <li>İptal edilen faturalar sayılmaz, iadeler cirodan düşülür.</li>
            {aylik?.ilkFaturaTarihi ? <li>Panelde {formatTarih(aylik.ilkFaturaTarihi)} ve sonrasının faturaları var.</li> : null}
          </ul>
        </details>
      </div>

      {hata && <div className="ccd-alert ccd-alert-danger">{hata}</div>}

      {bakiye && (
        <div className="ccd-grid ccd-grid-3">
          <button type="button" className="ccd-card ccd-stat-tik" data-fihrist="musteri-alacak-toplam" onClick={() => setSekme('musteri')}><div className="ccd-meta">Müşterilerden alacağımız <Ipucu id="musteri-alacak-toplam" /></div>
            <div className="ccd-display" style={{ color: 'var(--ccd-danger)' }}>{formatTl(bakiye.ozet.alacakKurus)}</div>
            <div className="ccd-meta">Paraşüt müşteri bakiyelerinden · listele →</div></button>
          <button type="button" className="ccd-card ccd-stat-tik" data-fihrist="tedarikci-borcu" onClick={() => setSekme('tedarikci')}><div className="ccd-meta">Ödenmemiş alış faturaları <Ipucu id="tedarikci-borcu" /></div>
            <div className="ccd-display">{formatTl(bakiye.ozet.borcKurus)}</div>
            <div className="ccd-meta">Paneldeki alış faturalarının kalanı · listele →</div></button>
          {bakiye.ozet.faturasizKurus != null && (
            <div className="ccd-card" data-fihrist="faturasiz-alacak"><div className="ccd-meta">Faturasız açık alacak <Ipucu id="faturasiz-alacak" /></div>
              <div className="ccd-display">{formatTl(bakiye.ozet.faturasizKurus)}</div>
              <div className="ccd-meta">Panelde girilen çıkışlardan · yukarıdakine dahil değil</div></div>
          )}
        </div>
      )}

      <div className="ccd-row-between" style={{ flexWrap: 'wrap' }}>
        <div className="ccd-tabs" role="tablist" style={{ minWidth: 0, maxWidth: '100%' }}>
          <button role="tab" aria-selected={sekme === 'cari'} className="ccd-tab" onClick={() => setSekme('cari')}>Cari ciro ve hedef</button>
          <button role="tab" aria-selected={sekme === 'kategori'} className="ccd-tab" onClick={() => setSekme('kategori')}>Stok tutmayan kategoriler</button>
          <button role="tab" aria-selected={sekme === 'musteri'} className="ccd-tab" onClick={() => setSekme('musteri')}>
            Müşteri bakiyeleri{bakiye ? ` (${bakiye.musteriler.length})` : ''}</button>
          <button role="tab" aria-selected={sekme === 'tedarikci'} className="ccd-tab" onClick={() => setSekme('tedarikci')}>
            Tedarikçi bakiyeleri{bakiye ? ` (${bakiye.tedarikciler.length})` : ''}</button>
        </div>
        {(sekme === 'cari' || sekme === 'kategori') && (
          <select className="ccd-select" style={{ maxWidth: 110 }} value={yil} onChange={(e) => setYil(Number(e.target.value))} aria-label="Yıl">
            {/* Son üç yıl + tanımlı dönemler (erken açılan gelecek yılın hedefi buradan yazılır). */}
            {[...new Set([...[0, 1, 2].map((n) => new Date().getFullYear() - n), ...donemYillari.filter((y) => y > new Date().getFullYear() - 3)])].sort((a, b) => b - a).map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
        )}
        {sekme !== 'kategori' && (
          <input className="ccd-input" style={{ maxWidth: 260 }} placeholder="Cari adı ara…" value={ara} onChange={(e) => setAra(e.target.value)} />
        )}
      </div>

      {sekme === 'cari' && (!cariYil ? <div className="ccd-empty">Yükleniyor…</div> : (
        <>
          <div className="ccd-grid ccd-grid-3">
            <div className="ccd-card" data-fihrist="ciro"><div className="ccd-meta">{cariYil.yil} cirosu (KDV hariç) <Ipucu id="ciro" /></div>
              <div className="ccd-display">{formatTl(cariYil.ozet.ciroKurus)}</div>
              <div className="ccd-meta">{cariYil.ozet.cariAdet} cariye satış</div></div>
            <div className="ccd-card" data-fihrist="hedefli-cariler"><div className="ccd-meta">Hedef verilen cariler <Ipucu id="hedefli-cariler" /></div>
              <div className="ccd-display">{cariYil.ozet.hedefKurus ? `%${Math.round((cariYil.ozet.hedefliCiroKurus / cariYil.ozet.hedefKurus) * 100)}` : '—'}</div>
              <div className="ccd-meta">{cariYil.ozet.hedefliAdet
                ? `${formatTl(cariYil.ozet.hedefliCiroKurus)} / ${formatTl(cariYil.ozet.hedefKurus)} · ${cariYil.ozet.hedefliAdet} cari`
                : 'Henüz hedef girilmedi — tabloda Hedef sütununa tıklayın'}</div></div>
            <div className="ccd-card" data-fihrist="yilin-gecen-kismi"><div className="ccd-meta">Yılın geçen kısmı <Ipucu id="yilin-gecen-kismi" /></div>
              <div className="ccd-display">%{Math.round(cariYil.yilGecenPct)}</div>
              <div className="ccd-meta">Gerçekleşme bunun altındaysa cari hedefin gerisinde</div></div>
          </div>
          {suz(cariYil.cariler).length === 0 ? <div className="ccd-empty">Bu yıl için kayıt yok.</div> : (
            <div className="ccd-card" style={{ padding: 0, overflowX: 'auto' }}>
              <table className="ccd-table">
                <thead><tr className="ccd-tr">
                  <th className="ccd-th">Cari</th>
                  <th className="ccd-th ccd-td-num">{cariYil.yil} cirosu <Ipucu id="ciro" /></th>
                  <th className="ccd-th ccd-td-num">Yıllık hedef <Ipucu id="yillik-hedef" /></th>
                  <th className="ccd-th" style={{ minWidth: 170 }}>Gerçekleşme <Ipucu id="yillik-hedef" /></th>
                  <th className="ccd-th ccd-td-num">Tahsil edilmemiş <Ipucu id="tahsil-edilmemis" /></th>
                  <th className="ccd-th ccd-td-num">Açık teklif <Ipucu id="acik-teklif" /></th>
                  <th className="ccd-th">Sorumlu <Ipucu id="sorumlu" /></th>
                </tr></thead>
                <tbody>
                  {suz(cariYil.cariler).map((c) => {
                    const geride = c.gerceklesmePct != null && c.beklenenPct != null && c.gerceklesmePct < c.beklenenPct;
                    return (
                      <tr key={c.id} className="ccd-tr">
                        <td className="ccd-td"><Link to={`/crm/${c.id}`}><b>{c.name}</b></Link>
                          <div className="ccd-meta">{c.faturaAdet} fatura{c.sonFatura ? ` · son ${formatTarih(c.sonFatura)}` : ''}</div></td>
                        <td className="ccd-td ccd-td-num"><b>{formatTl(c.ciroKurus)}</b></td>
                        <td className="ccd-td ccd-td-num">
                          {hedefDuzen?.id === c.id ? (
                            <form onSubmit={(e) => { e.preventDefault(); const v = hedefDuzen.deger.trim();
                              void cariYaz(c.id, { hedefTl: v === '' ? null : Number(v.replace(/\./g, '').replace(',', '.')) }); setHedefDuzen(null); }}>
                              <input className="ccd-input" autoFocus style={{ width: 130, textAlign: 'right' }} inputMode="decimal" placeholder="TL"
                                     value={hedefDuzen.deger} onChange={(e) => setHedefDuzen({ id: c.id, deger: e.target.value })}
                                     onBlur={() => setHedefDuzen(null)} />
                            </form>
                          ) : yazabilir ? (
                            <button className="ccd-btn ccd-btn-ghost ccd-btn-sm" title="Hedefi değiştir"
                                    onClick={() => setHedefDuzen({ id: c.id, deger: c.hedefKurus ? String(c.hedefKurus / 100) : '' })}>
                              {c.hedefKurus ? formatTl(c.hedefKurus) : '+ hedef'}
                            </button>
                          ) : c.hedefKurus ? formatTl(c.hedefKurus) : <span className="ccd-muted">—</span>}
                        </td>
                        <td className="ccd-td">
                          {c.gerceklesmePct == null ? <span className="ccd-muted">hedef yok</span> : (
                            <>
                              <div style={{ height: 8, borderRadius: 4, background: 'var(--ccd-border)', position: 'relative', overflow: 'hidden' }}>
                                <div style={{ position: 'absolute', inset: 0, width: `${Math.min(100, c.gerceklesmePct)}%`, borderRadius: 4,
                                  background: c.gerceklesmePct >= 100 ? 'var(--ccd-success, #2e7d4f)' : geride ? 'var(--ccd-danger)' : 'var(--ccd-accent, #c2703f)' }} />
                              </div>
                              <div className="ccd-meta">%{c.gerceklesmePct}{c.gerceklesmePct >= 100 ? ' · hedef tamam' : geride ? ' · geride' : ' · yolunda'}</div>
                            </>
                          )}
                        </td>
                        <td className="ccd-td ccd-td-num">{c.kalanKurus ? formatTl(c.kalanKurus) : <span className="ccd-muted">—</span>}</td>
                        <td className="ccd-td ccd-td-num">{c.acikTeklifAdet ? <>{formatTl(c.acikTeklifKurus)} <span className="ccd-meta">· {c.acikTeklifAdet}</span></> : <span className="ccd-muted">—</span>}</td>
                        <td className="ccd-td">
                          {yazabilir ? (
                            <select className="ccd-select" style={{ minWidth: 130 }} value={c.sorumluId ?? ''}
                                    onChange={(e) => void cariYaz(c.id, { sorumluId: e.target.value ? Number(e.target.value) : null })}>
                              <option value="">— yok —</option>
                              {cariYil.kullanicilar.map((k) => <option key={k.id} value={k.id}>{k.name}</option>)}
                            </select>
                          ) : c.sorumlu ?? <span className="ccd-muted">—</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          <div className="ccd-meta">Hedef KDV hariç yıllık cirodur. Hedefe tıklayıp tutarı yazın, Enter'a basın; boş bırakırsanız hedef kalkar.</div>
        </>
      ))}

      {sekme === 'kategori' && (!kat ? <div className="ccd-empty">Yükleniyor…</div> : (
        <>
          <div className="ccd-meta">
            Kategori Paraşüt'te faturaya seçilen "Fatura / Gider kategorisi"dir; panel yalnız okur
            {kat.sonTazeleme ? ` (son çekim ${formatTarih(kat.sonTazeleme)})` : ' (ilk çekim bekleniyor)'}.
            Paraşüt'te kategori değişince en geç 30 dakikada buraya yansır.
            <b> Stok tutmaz</b> işaretlediğiniz kategorideki faturalar (yemek kartı, yakıt, nakliye, kesim hizmeti…) ürün eşleme kuyruğuna girmez ve stok hareketi yazmaz; işareti kaldırınca geri döner.
          </div>
          <div className="ccd-grid ccd-grid-2">
            {([['Satış — fatura kategorisi', kat.satis, 'satis'], ['Alış ve gider — gider kategorisi', kat.alis, 'alis']] as const).map(([baslik, liste, tur]) => {
              const toplam = liste.reduce((x, k) => x + k.haricKurus, 0) || 1;
              return (
                <div key={baslik} className="ccd-card" style={{ padding: 0, overflowX: 'auto' }}>
                  <table className="ccd-table">
                    <thead><tr className="ccd-tr">
                      <th className="ccd-th">{baslik} <Ipucu id={tur === 'satis' ? 'kategori-satis' : 'gider-kategorisi'} /></th>
                      <th className="ccd-th ccd-td-num">Tutar (KDV hariç) <Ipucu id={tur === 'satis' ? 'ciro' : 'alis-toplami'} /></th>
                      <th className="ccd-th ccd-td-num">Pay <Ipucu id="pay-birikimli" /></th>
                      <th className="ccd-th ccd-td-num">Kalan <Ipucu id={tur === 'satis' ? 'tahsil-edilmemis' : 'tedarikci-borcu'} /></th>
                      <th className="ccd-th">Stok tutmaz <Ipucu id="stok-tutmayan-kategori" /></th>
                    </tr></thead>
                    <tbody>
                      {liste.length === 0 ? <tr className="ccd-tr"><td className="ccd-td" colSpan={5}>Kayıt yok.</td></tr> : liste.map((k) => (
                        <tr key={k.ad} className="ccd-tr">
                          <td className="ccd-td">{k.ad === 'Kategorisiz' ? <span className="ccd-muted">Kategorisiz</span> : <b>{k.ad}</b>}
                            <div className="ccd-meta">{k.adet} fatura</div></td>
                          <td className="ccd-td ccd-td-num"><b>{formatTl(k.haricKurus)}</b></td>
                          <td className="ccd-td ccd-td-num">%{Math.round((k.haricKurus / toplam) * 100)}</td>
                          <td className="ccd-td ccd-td-num">{k.kalanKurus ? formatTl(k.kalanKurus) : <span className="ccd-muted">—</span>}</td>
                          <td className="ccd-td">
                            {k.ad !== 'Kategorisiz' && (
                              <input type="checkbox" checked={k.stokDisi} disabled={!yazabilir} aria-label={`${k.ad} stok tutmaz`}
                                     onChange={async (e) => {
                                       setHata(null);
                                       try {
                                         await api('/api/finans/stok-disi', { method: 'PUT', body: JSON.stringify({ tur, ad: k.ad, stokDisi: e.target.checked }) });
                                         setKat(await api(`/api/finans/kategoriler?yil=${yil}`));
                                       } catch (err) { setHata(err instanceof Error ? err.message : 'Kaydedilemedi'); }
                                     }} />
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              );
            })}
          </div>
        </>
      ))}

      {sekme === 'musteri' && (!bakiye ? <div className="ccd-empty">Yükleniyor…</div> : (
        <>
          <div className="ccd-meta">
            Bakiye Paraşüt'teki cari bakiyesidir{bakiye.bakiyeTarihi ? ` (son çekim ${formatTarih(bakiye.bakiyeTarihi)})` : ''}.
            Ada tıklayınca ekstre açılır.
          </div>
          {suz(bakiye.musteriler).length === 0 ? <div className="ccd-empty">Bakiyesi olan müşteri yok.</div> : (
            <div className="ccd-card" style={{ padding: 0, overflowX: 'auto' }}>
              <table className="ccd-table">
                <thead><tr className="ccd-tr">
                  <th className="ccd-th">Müşteri</th>
                  <th className="ccd-th ccd-td-num">Bakiye <Ipucu id="cari-bakiye" /></th>
                  <th className="ccd-th ccd-td-num">Açık fatura <Ipucu id="acik-fatura" /></th>
                  <th className="ccd-th">En eski vade <Ipucu id="acik-fatura" /></th>
                  {bakiye.faturasizGorunur && <th className="ccd-th ccd-td-num">Faturasız alacak <Ipucu id="faturasiz-alacak" /></th>}
                </tr></thead>
                <tbody>
                  {suz(bakiye.musteriler).map((m) => (
                    <tr key={m.id} className="ccd-tr">
                      <td className="ccd-td"><Link to={`/crm/${m.id}`}><b>{m.name}</b></Link>
                        {m.code && <div className="ccd-meta ccd-mono">{m.code}</div>}</td>
                      <td className="ccd-td ccd-td-num"><BakiyeHucre kurus={m.bakiyeKurus} /></td>
                      <td className="ccd-td ccd-td-num">{m.acikFaturaAdet ? <>{formatTl(m.acikFaturaKurus)} <span className="ccd-meta">· {m.acikFaturaAdet} fatura</span></> : <span className="ccd-muted">—</span>}</td>
                      <td className="ccd-td">{m.enEskiVade ? formatTarih(m.enEskiVade) : <span className="ccd-muted">—</span>}</td>
                      {bakiye.faturasizGorunur && <td className="ccd-td ccd-td-num">{m.faturasizKurus ? formatTl(m.faturasizKurus) : <span className="ccd-muted">—</span>}</td>}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      ))}

      {sekme === 'tedarikci' && bakiye && (
        <div className="ccd-meta">
          Ödenmemiş fatura paneldeki alış faturalarından hesaplanır ve kesindir. Paraşüt bakiyesi olduğu gibi gösterilir;
          artı/eksi yönünü Paraşüt'teki cari ekranıyla bir kez karşılaştırın.
        </div>
      )}
      {sekme === 'tedarikci' && (!bakiye ? <div className="ccd-empty">Yükleniyor…</div> : suz(bakiye.tedarikciler).length === 0 ? (
        <div className="ccd-empty">Bakiyesi olan tedarikçi yok.</div>
      ) : (
        <div className="ccd-card" style={{ padding: 0, overflowX: 'auto' }}>
          <table className="ccd-table">
            <thead><tr className="ccd-tr">
              <th className="ccd-th">Tedarikçi</th>
              <th className="ccd-th ccd-td-num">Ödenmemiş fatura <Ipucu id="tedarikci-borcu" /></th>
              <th className="ccd-th ccd-td-num">Paraşüt bakiyesi <Ipucu id="tedarikci-bakiye" /></th>
            </tr></thead>
            <tbody>
              {suz(bakiye.tedarikciler).map((t) => (
                <tr key={t.id} className="ccd-tr">
                  <td className="ccd-td"><b>{t.name}</b></td>
                  <td className="ccd-td ccd-td-num">{t.acikFaturaAdet ? <><b>{formatTl(t.acikFaturaKurus)}</b> <span className="ccd-meta">· {t.acikFaturaAdet} fatura</span></> : <span className="ccd-muted">—</span>}</td>
                  <td className="ccd-td ccd-td-num">{t.bakiyeKurus == null ? <span className="ccd-muted">—</span> : `${t.bakiyeKurus < 0 ? '−' : ''}${formatTl(Math.abs(t.bakiyeKurus))}`}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
}
